export interface AuthProfile {
  name: string;
  phone: string | null;
  photo?: string | null;
  birthday?: string | null;
  anniversary?: string | null;
}

// Module-level profile cache (reactive, shared across all uses)
const profile = ref<AuthProfile | null>(null);
const profileLoading = ref(false);
const profileLoaded = ref(false);

function getPostHogClient() {
  if (import.meta.server) return null;
  try {
    const nuxtApp = useNuxtApp();
    const ph = typeof (nuxtApp as any)?.$posthog === "function"
      ? (nuxtApp as any).$posthog()
      : (nuxtApp as any)?.$posthog || (window as any)?.posthog;
    return ph && (typeof ph.capture === "function" || typeof ph.identify === "function") ? ph : null;
  } catch {
    return (window as any)?.posthog || null;
  }
}

export function identifyPosthogUser(currentUser: any, currentProfile?: AuthProfile | null) {
  if (!import.meta.client || !currentUser) return;
  const userId = currentUser.id || currentUser.sub;
  if (!userId) return;

  const isAnon = currentUser.is_anonymous || currentUser.app_metadata?.provider === 'anonymous';
  const ph = getPostHogClient();
  if (ph && typeof ph.identify === 'function') {
    if (isAnon) {
      ph.identify(userId, {
        is_anonymous: true,
        supabase_user_id: userId,
        supabase_anon_id: userId,
      });
    } else {
      const meta = currentUser.user_metadata ?? {};
      const name = currentProfile?.name || meta.full_name || meta.display_name || meta.name || '';
      const email = currentUser.email || meta.email || '';
      const phone = currentProfile?.phone || meta.phone || '';

      const properties: Record<string, any> = {
        is_anonymous: false,
        supabase_user_id: userId,
      };
      if (email) {
        properties.email = email;
        properties.$email = email;
      }
      if (name) {
        properties.name = name;
        properties.$name = name;
      }
      if (phone) {
        properties.phone = phone;
        properties.$phone = phone;
      }

      ph.identify(userId, properties);
    }
  }
}

export function useAuth() {
  const supabase = useSupabaseClient();
  const user = useSupabaseUser();

  const isLoggedIn = computed(() => {
    if (!user.value) return false;
    if (user.value.is_anonymous) return false;
    if (user.value.app_metadata?.provider === 'anonymous') return false;
    return true;
  });

  // Fetch name/phone/photo from profiles table when user logs in
  async function fetchProfile() {
    if (!user.value || profileLoaded.value || profileLoading.value) return;

    // Ignore anonymous users
    if (user.value.is_anonymous || user.value.app_metadata?.provider === 'anonymous') return;

    profileLoading.value = true;
    try {
      const { data, error } = await (supabase as any)
        .from("profiles")
        .select("name, phone, photo, birthday, anniversary")
        .eq("id", user.value.id || user.value.sub)
        .single();

      if (!error && data) {
        // Prefer DB data (has formatted phone), fall back to user_metadata
        const meta = user.value.user_metadata ?? {};
        profile.value = {
          name: data.name || meta.full_name || meta.display_name || '',
          phone: data.phone ?? meta.phone ?? null,
          photo: data.photo ?? meta.avatar_url ?? null,
          birthday: data.birthday ?? meta.birthday ?? null,
          anniversary: data.anniversary ?? meta.anniversary ?? null,
        };
        identifyPosthogUser(user.value, profile.value);
      }
      // If DB query failed, profile is already set from user_metadata (see watch below)
      profileLoaded.value = true;
    } catch (e) {
      console.error("[useAuth] fetchProfile exception:", e);
    } finally {
      profileLoading.value = false;
    }
  }

  // Watch for user changes and reset profile cache on logout
  watch(
    user,
    (newUser) => {
      if (!newUser) {
        profile.value = null;
        profileLoaded.value = false;
        if (import.meta.client) {
          try {
            const ph = getPostHogClient();
            if (ph && typeof ph.reset === "function") {
              ph.reset();
            }
          } catch {}
        }
      } else {
        const isAnon = newUser.is_anonymous || newUser.app_metadata?.provider === 'anonymous';
        if (isAnon) {
          identifyPosthogUser(newUser);
        } else {
          const meta = newUser.user_metadata ?? {};
          const nameFromMeta = meta.full_name || meta.display_name || '';
          const phoneFromMeta = meta.phone ?? null;
          if (!profileLoaded.value && (nameFromMeta || phoneFromMeta)) {
            profile.value = {
              name: nameFromMeta,
              phone: phoneFromMeta,
              photo: meta.avatar_url ?? null,
              birthday: meta.birthday ?? null,
              anniversary: meta.anniversary ?? null,
            };
          }
          identifyPosthogUser(newUser, profile.value);
          if (!profileLoaded.value) {
            fetchProfile();
          }
        }
      }
    },
    { immediate: true },
  );

  async function signup(params: {
    name: string;
    email: string;
    phone: string;
    birthday?: string;
    anniversary?: string;
    password: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      let isAnon = user.value?.is_anonymous || user.value?.app_metadata?.provider === 'anonymous';
      let resolvedUserId = user.value?.id || (user.value as any)?.sub;

      // If useSupabaseUser() claims haven't hydrated yet, check the client supabase session
      if (!resolvedUserId) {
        try {
          const { data } = await supabase.auth.getSession();
          const sessionUser = data?.session?.user;
          if (sessionUser) {
            resolvedUserId = sessionUser.id || (sessionUser as any)?.sub;
            isAnon = sessionUser.is_anonymous || sessionUser.app_metadata?.provider === 'anonymous';
          }
        } catch {}
      }

      const body = {
        ...params,
        anonymousUserId: isAnon && resolvedUserId ? resolvedUserId : undefined,
      };

      await $fetch("/api/auth/signup", {
        method: "POST",
        body,
      });
      return { success: true };
    } catch (err: any) {
      console.error("[useAuth] signup error:", err);
      const message =
        err?.data?.message || err?.message || "Something went wrong. Please try again.";
      return { success: false, error: message };
    }
  }

  async function signin(
    email: string,
    password: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await $fetch<{ session: any }>("/api/auth/signin", {
        method: "POST",
        body: { email, password },
      });

      // Set the session on the client-side Supabase instance
      // so useSupabaseUser() becomes reactive immediately
      if (result.session) {
        await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });
      }

      // Reset profile cache so it re-fetches for the new user
      profile.value = null;
      profileLoaded.value = false;
      await fetchProfile();

      return { success: true };
    } catch (err: any) {
      console.error("[useAuth] signin caught error:", err);
      const message =
        err?.data?.message || err?.message || "Something went wrong. Please try again.";
      return { success: false, error: message };
    }
  }

  async function signinWithGoogleIdToken(token: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error, data } = await supabase.auth.signInWithIdToken({
        provider: "google",
        token: token,
      });
      if (error) throw error;

      // Update session if it succeeded
      if (data.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });

        // Sync the profile securely using the backend endpoint
        try {
          await $fetch("/api/auth/sync-google-profile", {
            method: "POST",
            body: { accessToken: data.session.access_token },
          });
        } catch (syncError) {
          console.error("[useAuth] Failed to sync Google profile:", syncError);
        }
      }

      // Reset profile cache
      profile.value = null;
      profileLoaded.value = false;
      await fetchProfile();

      return { success: true };
    } catch (err: any) {
      console.error("[useAuth] signinWithGoogleIdToken error:", err);
      return { success: false, error: err.message || "Failed to sign in with Google." };
    }
  }

  async function signinWithGoogle(): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${useRequestURL().origin}/`,
        },
      });
      if (error) throw error;
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || "Failed to sign in with Google." };
    }
  }

  async function signout(): Promise<void> {
    await supabase.auth.signOut();
    profile.value = null;
    profileLoaded.value = false;
    resetNudgeState();
  }

  async function resetPassword(email: string, redirectTo: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });
      if (error) throw error;
      return { success: true };
    } catch (err: any) {
      console.error("[useAuth] resetPassword error:", err);
      return { success: false, error: err.message || "Failed to send reset link." };
    }
  }

  async function updatePassword(password: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      return { success: true };
    } catch (err: any) {
      console.error("[useAuth] updatePassword error:", err);
      return { success: false, error: err.message || "Failed to update password." };
    }
  }

  async function verifyAndResetPassword(tokenHash: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      // 1. Verify the OTP (this creates the session)
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery',
      });
      if (verifyError) throw verifyError;

      // 2. Immediately update the password
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;

      // 3. Immediately sign out so they aren't auto-logged in
      await supabase.auth.signOut();
      profile.value = null;
      profileLoaded.value = false;

      return { success: true };
    } catch (err: any) {
      console.error("[useAuth] verifyAndResetPassword error:", err);
      return { success: false, error: err.message || "Failed to verify link or update password." };
    }
  }

  return {
    user,
    isLoggedIn,
    profile,
    profileLoading,
    fetchProfile,
    signup,
    signin,
    signinWithGoogle,
    signinWithGoogleIdToken,
    signout,
    resetPassword,
    updatePassword,
    verifyAndResetPassword,
  };
}
